/**
 * Typen des Umsatzsteuer-Rechenkerns (Spec 2026-09-11, Sektionen 3.1–3.3 und 4).
 *
 * Reines TypeScript: keine Angular-, RxJS- oder Firebase-Importe. Die Lite-Typen sind
 * das, was der Loader (Plan 2) aus Firestore-Dokumenten baut – Timestamps sind dort
 * bereits per normalizeToValidDateOrNull in `Date | null` gewandelt, fehlende Felder
 * sind explizit `null`, damit die Engine nie zwischen undefined und null unterscheiden muss.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import type {BundeslandCode} from '../../../../helpers/werktage';

// ---------------------------------------------------------------------------
// 3.1 Steuerprofil am Eigentümer
// ---------------------------------------------------------------------------

export type VatTaxStatus = 'STEUERFREI' | 'KLEINUNTERNEHMER' | 'REGELBESTEUERUNG';
export type VatForm = 'SOLL' | 'IST';
export type VatPeriodType = 'MONAT' | 'QUARTAL';
export type VatVoranmeldungszeitraum = 'MONAT' | 'QUARTAL' | 'BEFREIT';

export interface LandlordTaxProfileEntry {
    abJahr: number;
    status: VatTaxStatus;
    form: VatForm;
    voranmeldungszeitraum?: VatVoranmeldungszeitraum;   // je Abschnitt historisiert (nur Regelbesteuerung); fehlt → Profilwert
}

export interface LandlordTaxProfile {
    status: VatTaxStatus;                                    // Kopie des aktuellen Eintrags aus `besteuerung`
    besteuerung: LandlordTaxProfileEntry[];
    voranmeldungszeitraum: VatVoranmeldungszeitraum;
    dauerfristverlaengerung: boolean;
    sondervorauszahlung: Record<string, number>;             // Jahr → Euro
    steuernummer: string | null;
    steuernummerElster: string | null;
    bundesland: BundeslandCode | null;                       // Sitz des Finanzamts
    finanzamt: { name: string; bufaNr: string } | null;
    ustIdNr: string | null;
    steuerberater: { name: string; email: string } | null;
    setupCompletedAt: Date | null;
    updatedAt: Date;
    updatedBy: string;
    /**
     * Landing-Vorschlag „noch kein Steuerprofil“ für das ganze Team ausgeblendet (Landing v2). Wird NUR per dotted key
     * `taxProfile.suggestionDismissed` mit `update()` geschrieben/gelöscht (VatTaxProfileService) und von `resolveTaxProfile`
     * ignoriert – das Feld allein macht den Eigentümer nicht „eingerichtet“ (`setupCompletedAt` bleibt null).
     */
    suggestionDismissed?: VatSuggestionDismissed | null;
}

/** Marker „Vorschlag ausgeblendet“ am Steuerprofil: wer (uid + Anzeigename zum Zeitpunkt) und wann. */
export interface VatSuggestionDismissed {
    at: Date;
    by: string;
    byName: string | null;
}

// ---------------------------------------------------------------------------
// 3.2 USt-Kategorie am Mietverhältnis
// ---------------------------------------------------------------------------

/**
 * USt-Kategorie eines Mietverhältnisses (Spec 3.2, Invariante v2).
 * `SONSTIGER_SATZ` = „anderer Steuersatz“: der Satz steht frei in `costs.vat` (z. B. Österreich 20 %, Schweiz 8,1 %,
 * Gewerbe ohne USt 0 %). Sollstellungen und Belege rechnen damit; in der deutschen Voranmeldung zählt er nicht als
 * steuerpflichtiger Umsatz (kein Kz 81/86) – 0 % wird wie § 4 Nr. 12 (Kz 48) geführt, alles andere ausgeschlossen.
 */
export type PeriodVatCategory = 'STEUERFREI_4_12' | 'STEUERPFLICHTIG_19' | 'STEUERPFLICHTIG_7' | 'SONSTIGER_SATZ' | 'NICHT_STEUERBAR';
export type PeriodVatCategorySource = 'FORM' | 'INLINE' | 'IMPORT' | 'BACKFILL';

// ---------------------------------------------------------------------------
// 3.3 Vorsteuer-Profil je Immobilie (Collection vatPropertyProfiles/{propertyID})
// ---------------------------------------------------------------------------

export type InputTaxKeyMode = 'AUTO_FLAECHE' | 'MANUELL' | 'VOLL' | 'KEIN';

export interface InputTaxKey {
    mode: InputTaxKeyMode;
    manualShare: number | null;
    note: string | null;
}

export type VacancyIntent = 'STEUERPFLICHTIG' | 'STEUERFREI';

export interface PropertyVatProfile {
    id: string;
    propertyID: string;
    customerID: string;
    inputTaxKey: InputTaxKey;
    yearOverrides: Record<string, InputTaxKey>;              // Jahr → Schlüssel
    vacancyIntent: Record<string, VacancyIntent>;            // unitID → Verwendungsabsicht bei Leerstand
    dismissedHints: Record<string, { by: string; at: Date }>; // `${periodID}_${hintID}`
    createdAt: Date;
    updatedAt: Date;
    updatedBy: string;
}

// ---------------------------------------------------------------------------
// Lite-Eingabetypen (vom Loader aus Firestore-Dokumenten gebaut)
// ---------------------------------------------------------------------------

/**
 * Spiegel von BookingArtOptions in booking/models/booking.ts – Achtung: der Enum-Wert
 * BK ist als String 'BETRIEBSKOSTEN' gespeichert, GUTHABEN ist die veraltete Guthaben-Art
 * (seit 2024 durch BK mit Vorzeichen ersetzt) und wird wie BK behandelt.
 */
export type BookingArtLite =
    'WARM_MIETE' | 'KALT_MIETE' | 'KAUTION' | 'BETRIEBSKOSTEN'
    | 'DUNNING_FEES' | 'INTEREST_EXPENSES' | 'SP' | 'CUSTOM' | 'GUTHABEN';
export type BookingTypeLite = 'PAYMENT' | 'PAYOUT';
export type BookingStatusLite = 'PAID' | 'NOT_PAID' | 'PARTIALLY_PAID' | 'EXPECTED' | 'IGNORED';

export interface RentReductionLite {
    id: string;
    amount: number;              // immer positiv (RentReduction.amount)
    date: Date | null;           // Erfassungsdatum der Minderung
    createdAt: Date | null;      // Fallback für den Erfassungsmonat (R7)
}

export interface BookingLite {
    id: string;
    propertyID: string;
    unitID: string | null;
    periodID: string | null;
    date: Date | null;                       // Fälligkeit (Sollstellungsdatum)
    createdAt: Date | null;
    amount: number | null;                   // Soll (PAYOUT-Sollstellungen sind bereits negativ gespeichert)
    haben: number;                           // bisher gezahlt
    saldo: number;                           // haben − amount
    rentReductionAmount: number;             // Summe aller Minderungen
    rentReductionList: RentReductionLite[];  // einzelne Minderungen (leer, wenn nur die Summe bekannt ist)
    divisionWarm: { kalt: number | null; bk: number | null; hk: number | null } | null;
    art: BookingArtLite | null;
    type: BookingTypeLite | null;
    status: BookingStatusLite | null;
    text: string | null;
    sollMonth: string | null;                // 'yyyy-MM' (Auto-Soll-Cron), Vorrang bei der Leistungsmonat-Ermittlung
    includesVAT: boolean | null;
    vat: number | null;                      // 19, 7 oder Altdaten 0.19
}

export interface PaymentLite {
    id: string;
    bookingID: string | null;
    propertyID: string;
    unitID: string | null;
    periodID: string | null;
    date: Date | null;                       // Zuflusstag
    amount: number | null;                   // signiert (Rücklastschrift negativ, PAYOUT negativ)
    art: BookingArtLite | null;
    type: BookingTypeLite | null;
    isInternal: boolean;                     // Verrechnungs-Leg (seit 27.02.2026)
    transferType: string | null;
    text: string | null;
    includesVAT: boolean | null;
    vat: number | null;
}

export type ExpenseTypeLite = 'EXPENSE' | 'CREDIT';

export interface ExpenseLite {
    id: string;
    propertyID: string;                      // expense.property.id
    unitsID: string[];
    hasAllUnitsSelected: boolean;
    type: ExpenseTypeLite;
    amount: number | null;                   // Bruttobetrag (positiv gespeichert)
    includesVAT: boolean | null;
    vatRate: number | null;
    vatAmount: number | null;
    date: Date | null;
    invoiceDate: Date | null;
    serviceEndDate: Date | null;
    /** nahausBookingsAccount.accountType === 'LOANS' || isLoanInterest || isLoanRepayment (Loader setzt das Flag) */
    isLoanAccount: boolean;
    description: string | null;
    accountRefID: string | null;             // nahausBookingsAccount.refID (Kontierung, z. B. „4200“)
    accountLabel: string | null;             // nahausBookingsAccount.label
    accountType: string | null;              // nahausBookingsAccount.accountType als String
}

export interface PeriodLite {
    id: string;
    propertyID: string;
    unitID: string;
    landlordID: string | null;
    isCommercial: boolean | null;
    costs: { vat: number | null } | null;
    vatCategory: PeriodVatCategory | null;
    vatCategorySource: PeriodVatCategorySource | null;
    startDate: Date | null;                  // rental.startDate
    endDate: Date | null;                    // rental.endDate
    tenantsName: string | null;              // tenantsDisplayName ?? tenantsName (Anzeige im Flächen-Nachweis)
}

/** Eintrag der Flächen-Zeitleiste (alle Einträge, das Datum entscheidet); `startDate === null` = Ausgangsfläche „seit Beginn“. */
export interface UnitAreaChangeLite {
    startDate: Date | null;
    area: number;                            // ABSOLUTE Fläche in m² ab startDate (UnitAreaChange.value)
}

export interface UnitLite {
    id: string;
    propertyID: string;
    area: number | null;
    areaChanges: UnitAreaChangeLite[] | null; // sortiert (ohne startDate zuerst, dann aufsteigend); null = keine Zeitleiste
    label: string | null;                    // unit.label ?? unit.w_id_text ?? 'W'+w_id
    categoryType: string | null;             // PropertyUnitCategoryType als String
    landlordID: string | null;
}

export interface OwnershipWindow {
    propertyID: string;
    landlordID: string;
    from: Date | null;                       // null = seit jeher
    to: Date | null;                         // null = laufend; sonst exklusiv (= performAt des Wechsels)
}

export interface PendingLandlordChange {
    propertyID: string;
    performAt: Date;                         // SCHEDULED mit performAt < now
}

export type VatAdjustmentTargetType = 'BOOKING' | 'PAYMENT' | 'EXPENSE' | 'LINE';
export type VatAdjustmentAction =
    'EXCLUDE' | 'SET_CATEGORY' | 'SET_INPUT_TAX_SHARE' | 'DURCHLAUFEND' | 'FORDERUNGSAUSFALL' | 'NACHTRAG_17';

/** Identisch zu VatReturn['adjustments'][number] (Spec 3.4); Plan 2 verweist auf diesen Typ. */
export interface VatAdjustment {
    id: string;
    targetType: VatAdjustmentTargetType;
    targetID: string;
    action: VatAdjustmentAction;
    value: string | number | null;
    reason: string;
    uid: string;
    displayName: string;
    createdAt: Date;
}

export interface VatEngineRange {
    start: Date;
    end: Date;
    year: number;
    periodKey: string;                       // 'M01'…'M12' | 'Q1'…'Q4'
    periodType: VatPeriodType;
}

/** Vorjahres- und laufender Umsatz vor dem Zeitraum für den § 19-Monitor (Loader liefert Jahreswerte). */
export interface Monitor19Input {
    vorjahr: number;
    laufendVorRange: number;
}

export interface VatEngineInput {
    landlordID: string;
    range: VatEngineRange;
    profile: LandlordTaxProfile;
    profileAt: (d: Date) => { status: VatTaxStatus; form: VatForm };
    ownership: OwnershipWindow[];
    pendingChanges: PendingLandlordChange[];
    periods: PeriodLite[];
    units: UnitLite[];
    propertyProfiles: Record<string, PropertyVatProfile>;
    bookings: BookingLite[];
    payments: PaymentLite[];
    priorPayments: PaymentLite[];
    expenses: ExpenseLite[];
    adjustments: VatAdjustment[];
    now: Date;
    monitor19Input: Monitor19Input | null;   // Ergänzung gegenüber der Spec (siehe Plan-Abschnitt Self-Review)
}

// ---------------------------------------------------------------------------
// Ergebnis
// ---------------------------------------------------------------------------

export type VatKz = '81' | '86' | '48' | '66' | 'NONE';
export type VatRule =
    'SOLL' | 'IST' | 'MINDEST_IST' | 'MINDEST_IST_ABZUG' | 'MINDERUNG_17' | 'VERRECHNUNG'
    | 'VORSTEUER' | 'KU_ALTUMSATZ' | 'ADJUSTMENT' | 'AUSGESCHLOSSEN';
export type VatMonthSource = 'SOLL_MONTH' | 'TEXT' | 'DATE' | 'PAYMENT_DATE' | 'INVOICE_DATE' | 'SERVICE_END';
export type VatRate = 0 | 7 | 19;
export type VatSourceType = 'BOOKING' | 'PAYMENT' | 'EXPENSE';

export interface VatLineSource {
    type: VatSourceType;
    id: string;
    propertyID: string;
    unitID: string | null;
    periodID: string | null;
    deepLink: string;
}

export interface VatLine {
    id: string;                              // `${type}_${sourceID}_${rule}_${monthKey}`
    kz: VatKz;
    rule: VatRule;
    source: VatLineSource;
    monthKey: string;                        // 'yyyy-MM'
    monthSource: VatMonthSource;
    category: PeriodVatCategory | null;
    rate: VatRate;
    gross: number;
    net: number;
    tax: number;
    share: number;                           // 0…1 (Eigentümer- × Vorsteuer-Anteil)
    label: string;
    issueIDs: string[];
}

export type VatIssueSeverity = 'BLOCKER' | 'HINWEIS' | 'INFO';
export type VatIssueAction =
    'INLINE_CATEGORY' | 'INLINE_AREA' | 'INLINE_VACANCY' | 'INLINE_KEY' | 'OPEN_LINK' | 'ADJUST' | 'CONFIRM' | 'NONE';

/**
 * Prüfhinweis-Typen der Spec (Sektion 4) plus drei Ergänzungen, für die die Spec eine
 * Schwere, aber keinen Typ nennt: SOURCE_DATA_INVALID (R3 „fehlende date/amount → BLOCKER"),
 * CUSTOM_ART_TAXABLE (R3 „CUSTOM/SP steuerbar mit HINWEIS"), RUECKLASTSCHRIFT_INFO (T05 „SOLL
 * mit Rücklastschrift → unverändert + INFO").
 */
export type VatIssueType =
    // BLOCKER
    | 'PERIOD_MISSING' | 'AREA_MISSING' | 'LANDLORD_CHANGE_PENDING' | 'KU_LIMIT_EXCEEDED'
    | 'PROFILE_YEAR_MISMATCH' | 'SOURCE_DATA_INVALID' | 'CUSTOM_RATE_INVALID'
    // HINWEIS
    | 'VAT_FLAG_MISMATCH' | 'OPTION_9_ABS_2' | 'AUFTEILUNG_12_11' | 'VACANCY_INTENT' | 'EXPENSE_DEFAULT_VAT'
    | 'REVERSE_CHARGE_13B' | 'LANDLORD_CHANGE_SPLIT' | 'TAXABLE_AT_STEUERFREI_PROFILE' | 'KU_LIMIT_NEAR'
    | 'CUSTOM_ART_TAXABLE' | 'CUSTOM_RATE_EXCLUDED'
    // INFO
    | 'CATEGORY_DERIVED' | 'LEISTUNGSMONAT_AUS_DATUM' | 'EXPENSE_NO_INVOICE_DATE' | 'WARM_SPLIT_INCONSISTENT'
    | 'MINDEST_IST_INFO' | 'OFFENE_SOLLSTELLUNG_IST' | 'NEGATIVE_KZ' | 'RUECKLASTSCHRIFT_INFO' | 'CUSTOM_RATE_ZERO_STEUERFREI';

export interface VatIssue {
    id: string;
    type: VatIssueType;
    severity: VatIssueSeverity;
    title: string;
    text: string;
    action: VatIssueAction;
    deepLink: string | null;
    lineIDs: string[];
}

/** Identisch zu VatReturn['kennzahlen'] (Spec 3.4). */
export interface VatKennzahlen {
    kz81: number;
    kz86: number;
    kz48: number;
    kz66: number;
    kz39: number;
    kz83: number;
    steuer81Elster: number;
    steuer86Elster: number;
    steuer81Cent: number;
    steuer86Cent: number;
}

/** Identisch zu VatReturn['totals'] (Spec 3.4). */
export interface VatTotals {
    lineCount: number;
    bookingCount: number;
    paymentCount: number;
    expenseCount: number;
}

export interface InputTaxByProperty {
    propertyID: string;
    mode: string;                            // InputTaxKeyMode oder 'ADJUSTMENT'
    share: number;
    taxableArea: number;
    totalArea: number;
}

export interface Monitor19 {
    vorjahr: number;
    laufend: number;
    state: 'OK' | 'NAHE' | 'UEBERSCHRITTEN';
}

export const ENGINE_VERSION = 'ust-engine/1.0.0' as const;

export interface VatEngineResult {
    kennzahlen: VatKennzahlen;
    lines: VatLine[];
    issues: VatIssue[];
    inputTaxByProperty: InputTaxByProperty[];
    monitor19: Monitor19 | null;
    stats: VatTotals;
    inputHash: string;
    engineVersion: typeof ENGINE_VERSION;
}

// ---------------------------------------------------------------------------
// Regel-Infrastruktur (intern)
// ---------------------------------------------------------------------------

/** Aus VatEngineInput abgeleitete Indizes; wird an jede Regel-Datei gereicht (rule-context.ts). */
export interface RuleContext {
    landlordID: string;
    range: VatEngineRange;
    now: Date;
    profileAt: VatEngineInput['profileAt'];
    periodsByID: ReadonlyMap<string, PeriodLite>;
    periodsByUnit: ReadonlyMap<string, PeriodLite[]>;
    unitsByProperty: ReadonlyMap<string, UnitLite[]>;
    unitsByID: ReadonlyMap<string, UnitLite>;
    bookingsByID: ReadonlyMap<string, BookingLite>;
    paymentsByID: ReadonlyMap<string, PaymentLite>;          // payments ∪ priorPayments
    paymentsByBooking: ReadonlyMap<string, PaymentLite[]>;   // payments ∪ priorPayments, je Buchung nach Datum/ID sortiert
    expensesByID: ReadonlyMap<string, ExpenseLite>;
    propertyProfiles: Record<string, PropertyVatProfile>;
    adjustments: VatAdjustment[];
    ownership: OwnershipWindow[];
    pendingChanges: PendingLandlordChange[];
}

export interface RuleOutput {
    lines: VatLine[];
    issues: VatIssue[];
}

export const EMPTY_RULE_OUTPUT = (): RuleOutput => ({lines: [], issues: []});

export const mergeRuleOutputs = (...outputs: RuleOutput[]): RuleOutput => ({
    lines: outputs.flatMap(o => o.lines),
    issues: outputs.flatMap(o => o.issues),
});

// ---------------------------------------------------------------------------
// 3.4 VatReturn – Collection vatReturns/{id} (Lücke L1 aus Plan 3: von Plan 2 und 3 konsumiert)
// ---------------------------------------------------------------------------

export type VatReturnStatus = 'ENTWURF' | 'GEPRUEFT' | 'EXPORTIERT' | 'BERICHTIGT';
export type VatReturnChecklistState = 'OFFEN' | 'ERLEDIGT' | 'UEBERSPRUNGEN';

export interface VatReturnChecklistEntry {
    state: VatReturnChecklistState;
    note: string | null;
    by: string;
    at: Date;
}

export type VatReturnExportType = 'PDF' | 'KZ48_PDF' | 'EXCEL' | 'ELSTER_XML' | 'DATEV' | 'MAIL';

export interface VatReturnExport {
    type: VatReturnExportType;
    fileName: string;
    filePath: string | null;
    url: string | null;
    sha256: string;
    xmlContent: string | null;
    createdAt: Date;
    uid: string;
}

/** Doc-ID `${landlordID}_${year}_${periodKey}_v${version}`; entsteht beim ersten Schreibvorgang, Ansehen schreibt nichts. */
export interface VatReturn {
    id: string;
    customerID: string;
    landlordID: string;
    year: number;
    periodKey: string;                       // 'M01'…'M12' | 'Q1'…'Q4'
    periodType: VatPeriodType;
    version: number;
    korrekturVonID: string | null;
    korrigiertDurchID: string | null;
    vorherigerStatus: VatReturnStatus | null;
    startDate: Date;
    endDate: Date;
    dueDate: Date;
    status: VatReturnStatus;
    taxProfileSnapshot: LandlordTaxProfile | null;
    kennzahlen: VatKennzahlen | null;
    totals: VatTotals | null;
    checklist: Record<string, VatReturnChecklistEntry>;
    adjustments: VatAdjustment[];
    engineVersion: string;
    inputHash: string | null;
    bestaetigterInputHash: string | null;
    computedAt: Date | null;
    freigabe: { uid: string; displayName: string; at: Date; bestaetigung: string } | null;
    uebermittelt: { am: Date; von: string; transferticket: string | null } | null;
    exports: VatReturnExport[];
    fristen: { taskID: string | null; reminderID: string | null };
    createdAt: Date;
    updatedAt: Date;
    createdBy: string;
    updatedBy: string;
}
